import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Download, Building2, Calendar, ChevronRight, ShieldCheck, AlertCircle } from "lucide-react";
import { LogoBrand, Spinner } from "../components/Brand";
import { useApp, API } from "../AppContext";

const fmt = (n) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(n || 0);

export default function PublicQuoteView() {
  const { token } = useParams();
  const { lang, setLang } = useApp();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [action, setAction] = useState(null); // 'accept' | 'reject' | null
  const [form, setForm] = useState({ name: "", email: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [resultMsg, setResultMsg] = useState("");

  const load = () => axios.get(`${API}/public/quote/${token}`)
    .then(({ data }) => setQ(data))
    .catch((e) => setErr(e?.response?.data?.detail || (lang === "fr" ? "Lien invalide" : "Invalid link")));

  useEffect(() => { load(); }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/quote/${token}/${action}`, form);
      setResultMsg(data.status === "accepted"
        ? (lang === "fr" ? "✓ Soumission acceptée — l'entrepreneur a été notifié." : "✓ Quote accepted — the contractor has been notified.")
        : (lang === "fr" ? "Soumission refusée. L'entrepreneur a été notifié." : "Quote rejected. The contractor has been notified."));
      setAction(null);
      load();
    } catch (e) {
      setResultMsg(e?.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
    } finally {
      setSubmitting(false);
    }
  };

  const T = lang === "fr" ? {
    quote_for: "Soumission pour",
    project: "Projet",
    location: "Adresse",
    date: "Date",
    expires: "Expire le",
    scope: "Étendue des travaux",
    exclusions: "Exclusions",
    conditions: "Conditions",
    payment: "Modalités de paiement",
    description: "Description",
    qty: "Qté",
    unit: "Unité",
    total: "Total",
    subtotal: "Sous-total",
    gst: "TPS (5%)",
    qst: "TVQ (9,975%)",
    grand: "Total taxes incluses",
    download: "Télécharger en PDF",
    accept: "Accepter cette soumission",
    reject: "Refuser",
    accepted_badge: "Soumission acceptée",
    rejected_badge: "Soumission refusée",
    revoked: "Ce lien n'est plus actif",
    your_name: "Votre nom complet",
    your_email: "Votre courriel (optionnel)",
    your_note: "Message à l'entrepreneur (optionnel)",
    confirm_accept: "Confirmer l'acceptation",
    confirm_reject: "Confirmer le refus",
    cancel: "Annuler",
    secured: "Authentifié et sécurisé par PROGÈRE Devis AI",
  } : {
    quote_for: "Quote for",
    project: "Project",
    location: "Address",
    date: "Date",
    expires: "Expires on",
    scope: "Scope of work",
    exclusions: "Exclusions",
    conditions: "Conditions",
    payment: "Payment terms",
    description: "Description",
    qty: "Qty",
    unit: "Unit",
    total: "Total",
    subtotal: "Subtotal",
    gst: "GST (5%)",
    qst: "QST (9.975%)",
    grand: "Grand total (taxes incl.)",
    download: "Download as PDF",
    accept: "Accept this quote",
    reject: "Decline",
    accepted_badge: "Quote accepted",
    rejected_badge: "Quote declined",
    revoked: "This link is no longer active",
    your_name: "Your full name",
    your_email: "Your email (optional)",
    your_note: "Message to the contractor (optional)",
    confirm_accept: "Confirm acceptance",
    confirm_reject: "Confirm decline",
    cancel: "Cancel",
    secured: "Authenticated and secured by PROGÈRE Devis AI",
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "transparent" }}>
        <div className="glass-strong max-w-md p-10 text-center">
          <AlertCircle size={48} color="#C00000" className="mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold mb-2" style={{ color: "#F5F5F5" }}>{err}</h1>
          <p className="text-sm" style={{ color: "rgba(245,245,245,0.6)" }}>{T.revoked}</p>
        </div>
      </div>
    );
  }
  if (!q) return <div className="min-h-screen flex items-center justify-center" style={{ background: "transparent" }}><Spinner /></div>;

  const isClosed = q.share_status === "accepted" || q.share_status === "rejected";

  return (
    <div className="min-h-screen" style={{ background: "transparent" }} data-testid="public-quote-view">
      {/* Header */}
      <header className="sticky top-0 z-40">
        <div className="glass-card mx-3 sm:mx-4 mt-3 sm:mt-4 px-4 sm:px-6 py-3 flex items-center justify-between gap-3" style={{ borderRadius: 999 }}>
          <LogoBrand />
          <div className="flex items-center gap-3">
            <button onClick={() => setLang(lang === "fr" ? "en" : "fr")} className="text-xs px-3 py-1 rounded-full border border-white/10 hover:border-[#D4AF37]" data-testid="public-lang">
              {lang === "fr" ? "EN" : "FR"}
            </button>
            <a href={`${API}/public/quote/${token}/pdf`} target="_blank" rel="noreferrer" className="btn-ghost text-xs" data-testid="public-pdf">
              <Download size={12} /> PDF
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Status banner */}
        {q.share_status === "accepted" && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-5 rounded-2xl flex items-center gap-4"
            style={{ background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.35)" }}
            data-testid="status-accepted-banner">
            <CheckCircle2 size={28} color="#4ADE80" />
            <div>
              <div className="font-heading text-lg font-bold" style={{ color: "#4ADE80" }}>{T.accepted_badge}</div>
              <div className="text-xs" style={{ color: "rgba(245,245,245,0.6)" }}>{new Date(q.accepted_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}</div>
            </div>
          </motion.div>
        )}
        {q.share_status === "rejected" && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-5 rounded-2xl flex items-center gap-4"
            style={{ background: "rgba(192,0,0,0.1)", border: "1px solid rgba(192,0,0,0.35)" }}
            data-testid="status-rejected-banner">
            <XCircle size={28} color="#C00000" />
            <div>
              <div className="font-heading text-lg font-bold" style={{ color: "#FF6B6B" }}>{T.rejected_badge}</div>
              <div className="text-xs" style={{ color: "rgba(245,245,245,0.6)" }}>{new Date(q.rejected_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}</div>
            </div>
          </motion.div>
        )}
        {resultMsg && (
          <div className="mb-6 px-4 py-3 rounded-xl text-sm" style={{ background: "rgba(212,175,55,0.1)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.3)" }}>
            {resultMsg}
          </div>
        )}

        {/* Header card */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          className="glass-strong p-6 sm:p-10 mb-6 glow-gold relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10" style={{ background: "radial-gradient(circle, #D4AF37 0%, transparent 70%)", transform: "translate(30%, -30%)" }} />
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.3em] mb-2" style={{ color: "#D4AF37" }}>{T.quote_for}</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black mb-2" style={{ color: "#F5F5F5" }}>{q.client_name}</h1>
            <div className="font-heading text-lg mb-6" style={{ color: "rgba(245,245,245,0.7)" }}>{q.project_name}</div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
              <div>
                <div className="uppercase tracking-wider mb-1" style={{ color: "rgba(245,245,245,0.45)" }}># {T.quote_for.split(" ")[0]}</div>
                <div className="font-semibold" style={{ color: "#D4AF37" }}>{q.quote_number}</div>
              </div>
              {q.date && (
                <div>
                  <div className="uppercase tracking-wider mb-1" style={{ color: "rgba(245,245,245,0.45)" }}>{T.date}</div>
                  <div style={{ color: "#F5F5F5" }}>{new Date(q.date).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA")}</div>
                </div>
              )}
              {q.expiry_date && (
                <div>
                  <div className="uppercase tracking-wider mb-1" style={{ color: "rgba(245,245,245,0.45)" }}>{T.expires}</div>
                  <div style={{ color: "#F5F5F5" }}>{new Date(q.expiry_date).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA")}</div>
                </div>
              )}
              {q.project_address && (
                <div className="col-span-2">
                  <div className="uppercase tracking-wider mb-1" style={{ color: "rgba(245,245,245,0.45)" }}>{T.location}</div>
                  <div style={{ color: "#F5F5F5" }}>{q.project_address}</div>
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-white/5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(212,175,55,0.15)" }}>
                <Building2 size={18} color="#D4AF37" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{lang === "fr" ? "Soumis par" : "Submitted by"}</div>
                <div className="font-semibold" style={{ color: "#F5F5F5" }}>{q.owner.company}</div>
                <div className="text-xs" style={{ color: "rgba(245,245,245,0.55)" }}>{q.owner.name} · <a href={`mailto:${q.owner.email}`} style={{ color: "#D4AF37" }}>{q.owner.email}</a></div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Items table */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="glass-card p-6 sm:p-8 mb-6" data-testid="items-table">
          <h2 className="font-heading text-lg font-bold mb-4" style={{ color: "#D4AF37" }}>{T.scope}</h2>
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.45)" }}>
                  <th className="text-left py-3 px-2">#</th>
                  <th className="text-left py-3 px-2">{T.description}</th>
                  <th className="text-right py-3 px-2 hidden sm:table-cell">{T.qty}</th>
                  <th className="text-right py-3 px-2">{T.total}</th>
                </tr>
              </thead>
              <tbody>
                {q.items.map((it, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="py-3 px-2" style={{ color: "rgba(245,245,245,0.5)" }}>{it.item_number || i + 1}</td>
                    <td className="py-3 px-2" style={{ color: "#F5F5F5" }}>{it.description}</td>
                    <td className="py-3 px-2 text-right hidden sm:table-cell" style={{ color: "rgba(245,245,245,0.7)" }}>{it.quantity} {it.unit || ""}</td>
                    <td className="py-3 px-2 text-right font-semibold" style={{ color: "#D4AF37" }}>{fmt(it.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 pt-4 border-t border-white/5 max-w-sm ml-auto space-y-2 text-sm">
            <div className="flex justify-between" style={{ color: "rgba(245,245,245,0.7)" }}>
              <span>{T.subtotal}</span><span>{fmt(q.subtotal)}</span>
            </div>
            <div className="flex justify-between" style={{ color: "rgba(245,245,245,0.7)" }}>
              <span>{T.gst}</span><span>{fmt(q.gst)}</span>
            </div>
            <div className="flex justify-between" style={{ color: "rgba(245,245,245,0.7)" }}>
              <span>{T.qst}</span><span>{fmt(q.qst)}</span>
            </div>
            <div className="flex justify-between pt-3 mt-3 border-t border-[#D4AF37]/30">
              <span className="font-heading font-bold" style={{ color: "#F5F5F5" }}>{T.grand}</span>
              <span className="font-heading text-2xl font-black" style={{ color: "#D4AF37" }}>{fmt(q.total)}</span>
            </div>
          </div>
        </motion.div>

        {/* Notes / conditions */}
        {(q.scope_of_work || q.exclusions || q.conditions || q.payment_terms) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {q.scope_of_work && (
              <div className="glass-card p-5">
                <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "#D4AF37" }}>{T.scope}</div>
                <p className="text-sm whitespace-pre-line" style={{ color: "rgba(245,245,245,0.78)" }}>{q.scope_of_work}</p>
              </div>
            )}
            {q.exclusions && (
              <div className="glass-card p-5">
                <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "#D4AF37" }}>{T.exclusions}</div>
                <p className="text-sm whitespace-pre-line" style={{ color: "rgba(245,245,245,0.78)" }}>{q.exclusions}</p>
              </div>
            )}
            {q.conditions && (
              <div className="glass-card p-5">
                <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "#D4AF37" }}>{T.conditions}</div>
                <p className="text-sm whitespace-pre-line" style={{ color: "rgba(245,245,245,0.78)" }}>{q.conditions}</p>
              </div>
            )}
            {q.payment_terms && (
              <div className="glass-card p-5">
                <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "#D4AF37" }}>{T.payment}</div>
                <p className="text-sm whitespace-pre-line" style={{ color: "rgba(245,245,245,0.78)" }}>{q.payment_terms}</p>
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        {!isClosed && !action && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            className="glass-strong p-6 sm:p-8 mb-6 text-center" data-testid="action-buttons">
            <h3 className="font-heading text-xl font-bold mb-3" style={{ color: "#F5F5F5" }}>
              {lang === "fr" ? "Êtes-vous d'accord avec cette soumission ?" : "Do you agree with this quote?"}
            </h3>
            <p className="text-sm mb-6" style={{ color: "rgba(245,245,245,0.6)" }}>
              {lang === "fr" ? "Votre réponse sera immédiatement transmise à l'entrepreneur." : "Your response will be immediately sent to the contractor."}
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button onClick={() => setAction("accept")} className="btn-red px-8" data-testid="cta-accept">
                <CheckCircle2 size={16} /> {T.accept}
              </button>
              <button onClick={() => setAction("reject")} className="btn-ghost" data-testid="cta-reject">
                <XCircle size={16} /> {T.reject}
              </button>
            </div>
          </motion.div>
        )}

        {/* Action form */}
        {action && (
          <motion.form initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            onSubmit={submit} className="glass-strong p-6 sm:p-8 mb-6" data-testid="action-form">
            <h3 className="font-heading text-lg font-bold mb-4" style={{ color: action === "accept" ? "#4ADE80" : "#FF6B6B" }}>
              {action === "accept" ? T.confirm_accept : T.confirm_reject}
            </h3>
            <div className="space-y-3">
              <input className="input-dark" placeholder={T.your_name} required
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" />
              <input type="email" className="input-dark" placeholder={T.your_email}
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email" />
              <textarea className="input-dark" rows={3} placeholder={T.your_note}
                value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="form-note" />
            </div>
            <div className="flex gap-3 mt-5">
              <button type="button" onClick={() => setAction(null)} className="btn-ghost" disabled={submitting} data-testid="form-cancel">{T.cancel}</button>
              <button type="submit" className={action === "accept" ? "btn-red flex-1 justify-center" : "btn-ghost flex-1 justify-center"} disabled={submitting} data-testid="form-submit">
                {submitting ? <Spinner /> : action === "accept" ? <><CheckCircle2 size={14} /> {T.confirm_accept}</> : <><XCircle size={14} /> {T.confirm_reject}</>}
              </button>
            </div>
          </motion.form>
        )}

        {/* Footer */}
        <div className="text-center mt-10">
          <div className="inline-flex items-center gap-2 text-xs" style={{ color: "rgba(245,245,245,0.4)" }}>
            <ShieldCheck size={12} color="#D4AF37" /> {T.secured}
          </div>
        </div>
      </div>
    </div>
  );
}
